import React, { FC, useMemo } from 'react';

import { Button, Icon, VStack } from '@rbilabs/universal-components';
import { isString } from 'lodash-es';
import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import { A11yWrapper } from 'components/a11y-wrapper';
import BlockRenderer from 'components/sanity-block-renderer';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useCart } from 'hooks/use-cart';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';
import { isAnyWalmartOffer } from 'state/loyalty/hooks/utils/walmart';
import { LoyaltyOffer } from 'state/loyalty/types';
import { primitive, spacing } from 'styles/constants';

import { WalmartBadgeContainer } from './cart-item-offer';

const Content = VStack.withConfig({ flex: 1 });

const StyledBlockRenderer = BlockRenderer.withConfig({
  textProps: {
    fontWeight: 'bold',
    fontSize: 15,
    lineHeight: 'sm',
  },
});

const RemoveButton = Button.withConfig({
  borderRadius: 0,
  backgroundColor: 'transparent',
  _text: {
    fontSize: 'xs',
    color: primitive.$bbqBrown,
  },
  _disabled: {
    color: primitive.$greyFour,
  },
  _hover: {
    background: 'transparent',
  },
  _pressed: {
    background: 'transparent',
  },
});

export const CartItemOfferDiscount: FC<{
  selectedLoyaltyOffer: LoyaltyOffer;
}> = ({ selectedLoyaltyOffer }) => {
  const dispatch = useAppDispatch();
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const { cartEntries, calculateCartTotalWithDiscount, removeFromCart } = useCart();
  const isWalmartOfferSelected = isAnyWalmartOffer(selectedLoyaltyOffer);

  const onRemove = () => {
    dispatch(actions.loyalty.setSelectedOffer(null));
    dispatch(actions.loyalty.setShouldRefetchOffers(true));
    dispatch(actions.loyalty.setOffersFeedbackMap({}));
    dispatch(actions.loyalty.resetAppliedDiscountOffers());
    removeFromCart({ cartId: DISCOUNT_OFFER_CART_ID });
  };

  const { isValid = true, textOfferRequirementsNotMet = '' } = useMemo(() => {
    const available = checkLoyaltyOfferAvailability(selectedLoyaltyOffer);
    const ruleErrors = offersFeedbackMap[selectedLoyaltyOffer.loyaltyEngineId || ''];
    const hasRuleErrors = Boolean(ruleErrors?.length);
    const { isCartTotalNegative = false } = calculateCartTotalWithDiscount();

    let errorMessage: React.ReactNode | undefined;
    if (!available) {
      errorMessage = t('offerNotAvailableInRestaurantPos');
    } else if (isCartTotalNegative) {
      errorMessage = t('cartOfferRequirementsNotMetError');
    } else if (hasRuleErrors) {
      // Show first error only
      errorMessage = getEvaluationResultMessage({
        evaluationResult: ruleErrors[0],
        incentiveType: LoyaltyPromotionType.OFFER,
        cartEntries,
        forceOnlyStringMessage: true,
      });
    }

    return {
      isValid: available && !hasRuleErrors && !isCartTotalNegative,
      textOfferRequirementsNotMet: errorMessage,
    };
  }, [
    checkLoyaltyOfferAvailability,
    offersFeedbackMap,
    calculateCartTotalWithDiscount,
    selectedLoyaltyOffer,
    cartEntries,
  ]);

  const iconVariant = textOfferRequirementsNotMet ? 'errorV2' : 'checkFill';
  const offerMessageKey = isValid ? 'offerApplied' : 'offerNotApplied';
  const offerMessage = t(offerMessageKey);

  useLogUserErrorMessage({
    message: offerMessage,
    logWhenTrue: !isValid,
    severity: LogUserErrorSeverity.Recoverable,
    context: {
      selectedOfferName: selectedLoyaltyOffer?.name,
      textOfferRequirementsNotMet: isString(textOfferRequirementsNotMet)
        ? textOfferRequirementsNotMet
        : null,
    },
  });

  const offerStatusText = (
    <A11yWrapper>
      <Typography fontSize="2xs" lineHeight="sm">
        {offerMessage}
        {textOfferRequirementsNotMet && ` - ${textOfferRequirementsNotMet}`}
      </Typography>
    </A11yWrapper>
  );

  const offerName = <StyledBlockRenderer content={selectedLoyaltyOffer?.name?.localeRaw} />;

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: textOfferRequirementsNotMet
            ? primitive.$errorLight
            : primitive.$successLight,
        },
      ]}
    >
      <Icon
        alignSelf="center"
        marginRight="$2"
        marginLeft={spacing.$2}
        variant={iconVariant}
        size="4"
      />
      <Content>
        {isWalmartOfferSelected && <WalmartBadgeContainer marginBottom="$0_5" />}
        {offerName}
        {offerStatusText}
      </Content>
      <RemoveButton
        accessibilityLabel={`${t('remove')} ${selectedLoyaltyOffer.name} `}
        onPress={onRemove}
      >
        {t('remove')}
      </RemoveButton>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'row',
    paddingVertical: spacing.$2,
    paddingHorizontal: spacing.$1,
    marginBottom: spacing.$3,
    borderRadius: 8,
  },
});
